import { useContext } from 'react';
import {
  FormFillerContext,
  FormFillerContextValue,
  CheckboxFeaturesMap,
  CheckboxFeatureConfig,
  CheckboxFeatureKey,
} from './FormFillerProvider';
import { FormRole } from '../../types/formFiller';

/**
 * Hook to access the FormFiller context
 * @throws Error if used outside of FormFillerProvider
 */
function useFormFiller(): FormFillerContextValue {
  const context = useContext(FormFillerContext);
  if (!context) {
    throw new Error('useFormFiller must be used within a FormFillerProvider');
  }
  return context;
}

/**
 * Hook to access FormFiller state values
 */
export function useFormFillerInitialData() {
  const { initialData } = useFormFiller();
  return initialData;
}

export function useLoadedFormFillerInitialData() {
  const { initialData } = useFormFiller();
  if (initialData.status !== 'success') {
    throw new Error('useLoadedFormFillerInitialData must be used within a loaded FormFillerProvider');
  }
  return initialData;
}

export function useFormFillerSave() {
  const { handleSave } = useFormFiller();
  return handleSave;
}

export function useFormFillerSubmit() {
  const { handleSubmit } = useFormFiller();
  return handleSubmit;
}

export function useFormFillerSignature() {
  const ctx = useFormFiller();
  return {
    setSignature: ctx.setSignature,
    getSignatureImageUrl: (role: FormRole) => {
      if (role === 'Patient') {
        return ctx.signatureImageUrls.patient;
      } else if (role === 'Prescriber') {
        return ctx.signatureImageUrls.prescriber;
      } else {
        throw new Error(`Invalid role: ${role}`);
      }
    },
    uploadSignature: ctx.uploadSignature,
    allowSignatureUpload: ctx.initialData.status === 'success' ? ctx.initialData.allowSignatureUpload : false,
  };
}

export function useFormFillerUploadSignature() {
  const { uploadSignature } = useFormFiller();
  return uploadSignature;
}

export function useFormFillerRole() {
  const { role } = useFormFiller();
  return role;
}

export function useFormFillerToken() {
  const { token } = useFormFiller();
  return token;
}

export function useFormFillerSuccessScreen(): [boolean, (shouldShowSuccessScreen: boolean) => void] {
  const { shouldShowSuccessScreen, setShouldShowSuccessScreen } = useFormFiller();
  return [shouldShowSuccessScreen, setShouldShowSuccessScreen];
}

export function useFormFillerBasePdfDownloadUrl() {
  const { getBasePdfDownloadUrl } = useFormFiller();
  return getBasePdfDownloadUrl;
}

export function useFormFillerCompletedDocumentUrl() {
  const { getCompletedDocumentUrl } = useFormFiller();
  return getCompletedDocumentUrl;
}

/**
 * Hook to access all checkbox features configuration.
 * Returns the full features map from context.
 */
export function useCheckboxFeatures(): CheckboxFeaturesMap {
  const { checkboxFeatures } = useFormFiller();
  return checkboxFeatures;
}

/**
 * Hook to check if a specific checkbox feature is enabled and get its config.
 * Returns undefined if the feature is not enabled.
 */
export function useCheckboxFeature<K extends CheckboxFeatureKey>(featureKey: K): CheckboxFeatureConfig[K] | undefined {
  const checkboxFeatures = useCheckboxFeatures();
  return checkboxFeatures[featureKey];
}
